import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { getEnvironment, isCypressTest, isProd } from "./environment";
import { Role } from "../types/auth";

const clientToken = "pubfc50a230fa18be59172dac9542f089c2";
const site = "datadoghq.com";
const env = getEnvironment();

/**
 * Session replay sample rates by environment.
 *   production  – 100 % so every feedback-tagged session has a replay available.
 *   staging     – 100 % for full visibility during QA.
 *   development – 0 % to avoid noise and local overhead.
 */
const sessionReplaySampleRate = env === "development" ? 0 : 100;

export type LoggingUser = {
  id: string;
  name?: string;
  brand_id?: number;
  brand_name?: string;
  rmn_publisher_id?: number;
  role: Role | string;
  external_info?: {
    role?: string;
    is_agency?: boolean;
    seniority?: string;
  };
};

const DataDogManager = (() => {
  let isReady = false;
  let isInitialized = false;
  let isRumInitialized = false;
  let initRum = false;
  let pendingOperations: Array<() => void> = [];
  let service = "web"; // default to web for backwards compatibility
  const version = String(import.meta.env.VITE_APP_VERSION || "dev");

  const runOrQueue = (operation: () => void) => {
    if (!isReady) {
      pendingOperations.push(operation);
      return;
    }

    operation();
  };

  const flushPendingOperations = () => {
    const operations = pendingOperations;
    pendingOperations = [];

    operations.forEach((operation) => operation());
  };

  const isRealUser = (user: LoggingUser): boolean => {
    const isWebCustomer = user.role === "customer" && user.brand_id !== 4; // skip users in test brand TODO: Make this more robust
    const isMediaUser = user.role === "rmn_publisher";
    const isInternalUser = user.role === "internal";

    return (
      (isProd() && !isCypressTest() && (isWebCustomer || isMediaUser || isInternalUser)) ||
      (!isProd() && user.name === "michael.scott")
    );
  };

  const initLogs = () => {
    datadogLogs.init({
      clientToken,
      site,
      service,
      env,
      version,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      storeContextsAcrossPages: true // fixes issue with version not being sent on browser load events
    });
  };

  const initRUM = () => {
    datadogRum.init({
      applicationId: "53944f1d-a3ea-4971-822c-098b7b5a245a",
      clientToken,
      site,
      service,
      version,
      env,
      sessionSampleRate: 100,
      sessionReplaySampleRate,
      startSessionReplayRecordingManually: true,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: "allow",
      actionNameAttribute: "data-action-name",
      storeContextsAcrossPages: true // fixes issue with version not being sent on browser load events
    });

    datadogRum.startSessionReplayRecording();
  };

  const init = (serviceName: string, useRum: boolean) => {
    service = serviceName;
    initRum = useRum;
    initLogs();
    isReady = true;
    flushPendingOperations();
  };

  const updateUserInfo = (user: LoggingUser) => {
    runOrQueue(() => {
      if (isInitialized) {
        return;
      }

      datadogLogs.setUser(user);

      if (initRum && isRealUser(user)) {
        if (!isRumInitialized) {
          initRUM();
          isRumInitialized = true;
        }
        datadogRum.setUser(user);
      }

      isInitialized = true;
    });
  };

  const setUserProperty = (key: string, value: string | number | boolean) => {
    runOrQueue(() => {
      if (!isInitialized) {
        return;
      }

      datadogLogs.setUserProperty(key, value);
      if (isRumInitialized) {
        datadogRum.setUserProperty(key, value);
      }
    });
  };

  // ---------------------------------------------------------------------------
  // Custom Reporting – safe to call before RUM init (DD SDK buffers internally).
  // Guarded by `initRum` so we don't buffer actions for scenarios where RUM
  // was never requested.
  // ---------------------------------------------------------------------------

  /** Record a timing milestone relative to the current view start. */
  const reportTiming = (name: string) => {
    if (!initRum) return;
    datadogRum.addTiming(name);
  };

  /** Report a structured custom action with arbitrary context. */
  const reportCustomAction = (name: string, context?: Record<string, unknown>) => {
    if (!initRum) return;
    datadogRum.addAction(name, context);
  };

  return {
    init,
    updateUserInfo,
    setUserProperty,
    reportTiming,
    reportCustomAction
  };
})();

export default DataDogManager;
